/**
 * Compliance Requirements Resolver — IN-1.a
 * ─────────────────────────────────────────
 * Pure function. Returns the compliance/agreement/banking shape required
 * for a given contractor based on `entityType` (EN-1 classification).
 *
 * IN-1.a is **observation-only**:
 *   • no writers
 *   • no UI consumers yet
 *   • no dispatch enforcement
 *   • no vetting-gate changes
 *   • no document model
 *
 * Parent-aware fields ("inherited from parent") are surfaced in the
 * output for downstream IN-1.b/c/d to consume, but this resolver does
 * NOT itself enforce or verify parent compliance.
 *
 * Soft enforcement is the locked default for IN-1.b/c at launch
 * (advisory banners only; no dispatch blocking; no auto-suspension).
 *
 * Anchor: mem://architecture/network-execution-phase-1a
 *         RECONCILIATION.md §12 (IN-1.a)
 */

import type { Contractor } from "@/data/types";
import type {
  OnboardingComplianceRequirementRuleSet,
  OnboardingComplianceRequirementSettings,
  OnboardingSession,
} from "@/data/contractor/onboarding/types";

export type AgreementVariant =
  | "1099_msa"
  | "w2_offer_letter"
  | "branch_sublicense"
  | "crew_acknowledgment"
  | "helper_acknowledgment"
  | "certification_agreement";

export type BankingMode = "own" | "parent_routed" | "payroll" | "none";

export interface ComplianceRequirements {
  /** State/local contractor license required on the entity itself. */
  licenseRequired: boolean;
  /** GL / WC insurance required on the entity itself. */
  insuranceRequired: boolean;
  /** IRS W-9 required on the entity itself. */
  w9Required: boolean;
  /** Background check required on the individual / principal. */
  backgroundCheckRequired: boolean;
  /** Agreement variant the entity must sign. */
  agreementVariant: AgreementVariant;
  /**
   * Whether this entity must configure its own payout destination.
   * False for `parent_routed`, `payroll`, and `none`.
   */
  bankingRequired: boolean;
  /** How this entity gets paid (or doesn't, for `none`). */
  bankingMode: BankingMode;
  /**
   * True when one or more *Required flags are inheritable from the
   * parent (i.e. the entity itself does not carry the doc, the parent
   * does). IN-1.b/c will use this to render "Inherited from {parent}"
   * vs "Own document required".
   */
  inheritsFromParent: {
    license: boolean;
    insurance: boolean;
    w9: boolean;
  };
}

type ComplianceOverrideInput = Partial<
  Pick<
    ComplianceRequirements,
    | "licenseRequired"
    | "insuranceRequired"
    | "w9Required"
    | "backgroundCheckRequired"
  >
> & {
  certificationRequired?: boolean;
  inheritsFromParent?: Partial<ComplianceRequirements["inheritsFromParent"]>;
};

function isCrewScopedEntity(entityType: string | null | undefined): boolean {
  return entityType === "crew" || entityType === "helper";
}

function realContractorId(value: string | null | undefined): string | null {
  const id = String(value ?? "").trim();
  if (!id || id.startsWith("onboarding:")) return null;
  return id;
}

function normalizeRuleSet(
  ruleSet: OnboardingComplianceRequirementRuleSet | undefined,
): ComplianceOverrideInput | undefined {
  if (!ruleSet) return undefined;
  const override: ComplianceOverrideInput = {};

  if (typeof ruleSet.licenseRequired === "boolean") {
    override.licenseRequired = ruleSet.licenseRequired;
  }
  if (typeof ruleSet.insuranceRequired === "boolean") {
    override.insuranceRequired = ruleSet.insuranceRequired;
  }
  if (typeof ruleSet.w9Required === "boolean") {
    override.w9Required = ruleSet.w9Required;
  }
  if (typeof ruleSet.backgroundCheckRequired === "boolean") {
    override.backgroundCheckRequired = ruleSet.backgroundCheckRequired;
  }
  if (typeof ruleSet.certificationRequired === "boolean") {
    override.certificationRequired = ruleSet.certificationRequired;
  }

  const inheritsFromParent: Partial<ComplianceRequirements["inheritsFromParent"]> = {};
  if (typeof ruleSet.inheritsLicenseFromParent === "boolean") {
    inheritsFromParent.license = ruleSet.inheritsLicenseFromParent;
  }
  if (typeof ruleSet.inheritsInsuranceFromParent === "boolean") {
    inheritsFromParent.insurance = ruleSet.inheritsInsuranceFromParent;
  }
  if (typeof ruleSet.inheritsW9FromParent === "boolean") {
    inheritsFromParent.w9 = ruleSet.inheritsW9FromParent;
  }
  if (Object.keys(inheritsFromParent).length > 0) {
    override.inheritsFromParent = inheritsFromParent;
  }

  return Object.keys(override).length > 0 ? override : undefined;
}

function applyComplianceOverride(
  base: ComplianceRequirements,
  override: ComplianceOverrideInput | undefined,
): ComplianceRequirements {
  if (!override) return base;
  return {
    ...base,
    licenseRequired: override.licenseRequired ?? base.licenseRequired,
    insuranceRequired: override.insuranceRequired ?? base.insuranceRequired,
    w9Required: override.w9Required ?? base.w9Required,
    backgroundCheckRequired:
      override.backgroundCheckRequired ?? base.backgroundCheckRequired,
    agreementVariant:
      override.certificationRequired === true
        ? "certification_agreement"
        : override.certificationRequired === false &&
            base.agreementVariant === "certification_agreement"
          ? "1099_msa"
          : base.agreementVariant,
    inheritsFromParent: {
      ...base.inheritsFromParent,
      ...(override.inheritsFromParent ?? {}),
    },
  };
}

function findParentOnboardingSession(
  session: Pick<OnboardingSession, "parentEntityId">,
  allSessions: Array<
    Pick<OnboardingSession, "contractorId" | "entityType" | "draft">
  >,
): Pick<OnboardingSession, "draft"> | undefined {
  const parentEntityId = realContractorId(session.parentEntityId);
  if (!parentEntityId) return undefined;
  return allSessions.find((candidate) => {
    if (isCrewScopedEntity(candidate.entityType)) return false;
    return realContractorId(candidate.contractorId) === parentEntityId;
  });
}

export function getOnboardingComplianceRequirementSettings(
  session: Pick<OnboardingSession, "entityType" | "parentEntityId" | "draft">,
  allSessions: Array<
    Pick<OnboardingSession, "contractorId" | "entityType" | "draft">
  > = [],
): OnboardingComplianceRequirementSettings | undefined {
  const localSettings = session.draft.complianceRequirementSettings;
  if (!isCrewScopedEntity(session.entityType)) {
    return localSettings;
  }
  if (localSettings?.crew || localSettings?.installer) {
    return localSettings;
  }
  return findParentOnboardingSession(session, allSessions)?.draft
    .complianceRequirementSettings;
}

export function getOnboardingComplianceRequirementOverride(
  session: Pick<OnboardingSession, "entityType" | "parentEntityId" | "draft">,
  allSessions: Array<
    Pick<OnboardingSession, "contractorId" | "entityType" | "draft">
  > = [],
): ComplianceOverrideInput | undefined {
  const settings = getOnboardingComplianceRequirementSettings(session, allSessions);
  if (!settings) return undefined;
  return isCrewScopedEntity(session.entityType)
    ? normalizeRuleSet(settings.crew)
    : normalizeRuleSet(settings.installer);
}

/**
 * Resolve compliance requirements for a contractor.
 *
 * @param contractor The contractor to resolve requirements for.
 * @param _parent    The parent entity, when `parentEntityId` is set.
 *                   Reserved for IN-1.b/c parent-aware enforcement;
 *                   IN-1.a only references its presence indirectly via
 *                   the inheritance flags.
 */
export function getRequiredCompliance(
  contractor: Pick<Contractor, "entityType" | "parentEntityId">,
  _parent?: Pick<Contractor, "id" | "entityType">,
  override?: ComplianceOverrideInput,
): ComplianceRequirements {
  // Safe fallback — unknown / missing entityType is treated as the
  // independent 1099 contractor baseline. Background checks and banking
  // verification are optional review signals, not required compliance gates.
  const entityType = contractor?.entityType ?? "independent";

  switch (entityType) {
    case "independent":
      return applyComplianceOverride({
        licenseRequired: true,
        insuranceRequired: true,
        w9Required: true,
        backgroundCheckRequired: false,
        agreementVariant: "1099_msa",
        bankingRequired: false,
        bankingMode: "own",
        inheritsFromParent: { license: false, insurance: false, w9: false },
      }, override);

    case "licensed_contractor":
      return applyComplianceOverride({
        licenseRequired: true,
        insuranceRequired: true,
        w9Required: true,
        backgroundCheckRequired: false,
        agreementVariant: "1099_msa",
        bankingRequired: false,
        bankingMode: "own",
        inheritsFromParent: { license: false, insurance: false, w9: false },
      }, override);

    case "internal_w2":
      return applyComplianceOverride({
        // Org-level docs live on the parent organization, not the W-2
        // employee record itself.
        licenseRequired: false,
        insuranceRequired: false,
        w9Required: false,
        backgroundCheckRequired: false,
        agreementVariant: "w2_offer_letter",
        bankingRequired: false,
        bankingMode: "payroll",
        inheritsFromParent: { license: false, insurance: false, w9: false },
      }, override);

    case "branch":
      // Branches operate under the parent's license, insurance, and
      // W-9. They still need their own sublicense agreement.
      return applyComplianceOverride({
        licenseRequired: true,
        insuranceRequired: true,
        w9Required: true,
        backgroundCheckRequired: false,
        agreementVariant: "branch_sublicense",
        bankingRequired: false,
        bankingMode: "parent_routed",
        inheritsFromParent: { license: true, insurance: true, w9: true },
      }, override);

    case "crew":
      return applyComplianceOverride({
        licenseRequired: false,
        insuranceRequired: true, // covered under parent's policy
        w9Required: false,
        backgroundCheckRequired: false,
        agreementVariant: "crew_acknowledgment",
        bankingRequired: false,
        bankingMode: "parent_routed",
        inheritsFromParent: { license: false, insurance: true, w9: false },
      }, override);

    case "helper":
      return applyComplianceOverride({
        licenseRequired: false,
        insuranceRequired: true, // covered under parent's policy
        w9Required: false,
        backgroundCheckRequired: false,
        agreementVariant: "helper_acknowledgment",
        bankingRequired: false,
        bankingMode: "parent_routed",
        inheritsFromParent: { license: false, insurance: true, w9: false },
      }, override);

    case "manufacturer_partner":
      return applyComplianceOverride({
        licenseRequired: true,
        insuranceRequired: true,
        w9Required: true,
        // Varies by certification program; default false until a
        // certification-specific override layer exists.
        backgroundCheckRequired: false,
        agreementVariant: "certification_agreement",
        bankingRequired: false,
        bankingMode: "own",
        inheritsFromParent: { license: false, insurance: false, w9: false },
      }, override);

    default: {
      // Exhaustiveness guard — if the EN-1 union grows, TypeScript will
      // surface the missing case here. At runtime, unknown values fall
      // through to the independent-contractor baseline.
      const _exhaustive: never = entityType;
      void _exhaustive;
      return applyComplianceOverride({
        licenseRequired: true,
        insuranceRequired: true,
        w9Required: true,
        backgroundCheckRequired: false,
        agreementVariant: "1099_msa",
        bankingRequired: false,
        bankingMode: "own",
        inheritsFromParent: { license: false, insurance: false, w9: false },
      }, override);
    }
  }
}

export function getRequiredOnboardingCompliance(
  session: Pick<OnboardingSession, "entityType" | "parentEntityId" | "draft">,
  allSessions: Array<
    Pick<OnboardingSession, "contractorId" | "entityType" | "draft">
  > = [],
  parent?: Pick<Contractor, "id" | "entityType">,
): ComplianceRequirements {
  return getRequiredCompliance(
    session,
    parent,
    getOnboardingComplianceRequirementOverride(session, allSessions),
  );
}
